import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { IS_USE_CENTIMETERS_SIZE_COUNTRY } from '@/constants/is-use-centimeters-size-country';
import { Tooltip } from 'antd';
import Image from 'next/image';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ProductRoot } from '../types/product';

export const CustomizedSize = ({
  shippingType,
  product,
}: {
  shippingType: 'Dresses' | 'Wedding Dresses';
  product: ProductRoot;
}) => {
  const form = useFormContext();
  const { watch, trigger, getValues } = form;
  const selectedSize = watch('Size');
  const isFloorLengthSizeDress =
    product.show_attribute?.find(item => item.code === 'aw_dresses_hemline')?.value === 'Floor Length';
  const aw_dresses_hemline_label = product?.show_attribute.find(item => item.code === 'aw_dresses_hemline')?.value as
    | 'Ankle Length'
    | 'Floor Length'
    | 'Hi Lo'
    | 'Knee Length'
    | 'Mini with Overskirt'
    | 'Mini/Short'
    | 'Tea Length';

  const imageSrc = useMemo(() => {
    if (shippingType !== 'Dresses') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/size-chart/measuring-guide-1.svg`;
    }

    if (aw_dresses_hemline_label === 'Ankle Length') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/ankle-length.png`;
    }

    if (aw_dresses_hemline_label === 'Floor Length') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/floor-length.png`;
    }

    if (aw_dresses_hemline_label === 'Hi Lo') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/hi-lo.png`;
    }

    if (aw_dresses_hemline_label === 'Knee Length') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/knee-length.png`;
    }

    if (aw_dresses_hemline_label === 'Mini with Overskirt') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/dress.png`;
    }

    if (aw_dresses_hemline_label === 'Mini/Short') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/mini-short.png`;
    }

    if (aw_dresses_hemline_label === 'Tea Length') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/tea-length.png`;
    }

    return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/dress.png`;
  }, [aw_dresses_hemline_label, shippingType]);

  return (
    selectedSize?.label === 'customized_size' && (
      <div className='mt-10 flex flex-col-reverse items-center lg_flex-row lg_items-start'>
        <div className='flex flex-col gap-[16px]'>
          <div className='space-y-4 '>
            {[
              {
                name: 'bust',
                label: 'Bust:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '76-173 cm' : '30-68 inch',
              },
              {
                name: 'waist',
                label: 'Waist:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '56-178 cm' : '22-70 inch',
              },
              {
                name: 'hip',
                label: 'Hip:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '76-191 cm' : '30-75 inch',
              },
              {
                name: shippingType === 'Dresses' ? 'hollowtoHem' : 'hollowToFloor',
                label: shippingType === 'Dresses' ? 'Hollow to Hem:' : 'Hollow to Floor:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '76-178 cm' : '30-70 inch',
              },
              {
                name: 'height',
                label: 'Height:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '127-229 cm' : '50-90 inch',
              },
              {
                name: 'extraLength',
                label: 'Extra Length:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '0-15 cm' : '0-6 inch',
              },
            ].map(field => {
              if (!isFloorLengthSizeDress && field.name === 'extraLength') return null;

              return (
                <FormField
                  key={field.name}
                  control={form.control}
                  name={field.name}
                  render={({ field: inputField }) => (
                    <div>
                      <FormItem className='flex items-center'>
                        <FormLabel className=' flex w-[130px] items-center justify-start gap-1 px-0.5 pt-2.5  text-sm leading-none text-primary lg_text-sm'>
                          {field.label}
                        </FormLabel>
                        <FormControl>
                          <input
                            {...inputField}
                            onChange={e => {
                              const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                              const formattedValue = numericValue.replace(/(\..*)\./g, '$1');

                              inputField.onChange(formattedValue);
                            }}
                            onBlur={() => {
                              if (
                                (field.name === 'height' || field.name === 'hollowtoHem') &&
                                getValues('height') &&
                                getValues('hollowtoHem')
                              ) {
                                trigger('height');
                                trigger('hollowtoHem');
                              }
                            }}
                            className='border-input text-colorDefault disabled_text-subtle m-0 inline-flex h-[34px] w-[100px] max-w-full border-1 border-solid border-E7DED5 bg-white px-1  outline-none '
                          />
                        </FormControl>
                        <div className='text-gray-500 ml-[5px] flex items-center text-sm'>
                          {field.hint}{' '}
                          {field.label === 'Extra Length:' && (
                            <Tooltip
                              placement='right'
                              title={
                                'Extra length is used for floor-length dresses when you need extra length to fit your shoe heel. If you do not need it, just fill 0.'
                              }
                            >
                              <div className='ml-[5px] flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-[#e7ded5] text-primary'>
                                ?
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      </FormItem>
                      <FormMessage id={field.name} />
                    </div>
                  )}
                />
              );
            })}
          </div>

          {shippingType === 'Dresses' && (
            <span className='flex text-sm leading-none text-primary lg_text-sm'>* Measure in undergarments only.</span>
          )}
        </div>

        <Image
          className='m-center max-w-[40%] lg_w-auto lg_max-w-[210px]'
          src={imageSrc}
          alt=''
          width={216}
          height={423}
        />
      </div>
    )
  );
};
